import {volumeUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import {
  FillPlateSourceChipStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableColumnType} from 'notebook/tables/TableConstants';
import BaseFillPlateTransferStructuredTableAdapter, {
  BaseFillPlateTransferStructuredTableMetadata,
  BaseFillPlateTransferStructuredTableRowMetadata,
} from 'samples/plate-maps/transfer-table/BaseFillPlateTransferStructuredTableAdapter';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface BaseDetailStructuredTableRowMetadata extends BaseFillPlateTransferStructuredTableRowMetadata {
  wellIndex: number;
  isContent: boolean;
  isEmpty: boolean;
  containerOrContainableId: string;
  containableId: string | null;
}

/**
 * Adapter describing and constraining the behavior of detailed transfer tables in modals.
 *
 * These table are never embedded in Notebook and never persisted. Their contents are determined by a
 * combination of preexisting values of Inventory items, user selections made in-modal, and plate records.
 */
export default class BaseDetailTransferStructuredTableAdapter<
  TTableMetadata extends BaseFillPlateTransferStructuredTableMetadata,
> extends BaseFillPlateTransferStructuredTableAdapter<TTableMetadata, BaseDetailStructuredTableRowMetadata> {
  getBlankRowMetadata(): BaseDetailStructuredTableRowMetadata {
    return {
      wellIndex: NaN,
      isContent: false,
      isEmpty: false,
      containerOrContainableId: '',
      containableId: null,
      schemaId: '',
    };
  }

  override injectFrontEndMetadata({column}: {column: StructuredTableColumn}): StructuredTableColumn {
    const newColumn = super.injectFrontEndMetadata({column});
    if (
      newColumn.columnType === TableColumnType.UNIT_REFERENCE &&
      newColumn.columnGroup === StructuredTableColumnGroups.FILL_PLATE_FINAL_QUANTITY
    ) {
      return {...newColumn, frontEndMetadata: {unitTypesFilterPredicate: volumeUnitTypePredicate}};
    }
    return newColumn;
  }

  override _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const baseColumns = super._getAllSupportedColumns(params);
    const sourceColumn: FillPlateSourceChipStructuredTableColumn = {
      ...makeStructuredTableColumn({
        name: 'Source',
        columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP,
        isReadOnly: true,
        width: 200,
      }),
      columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP,
      reagentGroupsById: params.metadata.reagentGroupsById,
      testOrderInfoById: params.metadata.testOrderInfoById,
    };
    return [
      makeStructuredTableColumn({
        name: 'Well',
        columnType: TableColumnType.STRING,
        isReadOnly: true,
        width: 40,
      }),
      baseColumns[0], // icon
      sourceColumn,
      ...baseColumns.slice(1),
      makeStructuredTableColumn({
        name: 'Final quantity',
        columnType: TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_FINAL_QUANTITY,
      }),
      makeStructuredTableColumn({
        name: 'Units',
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_FINAL_QUANTITY,
        width: 120,
      }),
    ];
  }
}
